import { useEffect, useMemo, useState } from 'react';

import {
  selectAlbumCascadeDefaults,
  selectPlaylistCascadeDefaults,
  selectRecordingCascadeDefaults,
  selectWorkCascadeDefaults,
} from '../store/slices/yourLibrary';
import { useAppSelector } from '../store/store';

export type CascadeEntityType = 'album' | 'recording' | 'playlist' | 'work';
export type CascadeMode = 'save' | 'remove';

const ENTITY_SCOPES = {
  album: ['album', 'recordings', 'tracks'] as const,
  recording: ['recording', 'tracks'] as const,
  playlist: ['playlist', 'recordings', 'tracks'] as const,
  work: ['work', 'recordings'] as const,
};

export type CascadeScope<TType extends CascadeEntityType> =
  (typeof ENTITY_SCOPES)[TType][number];

type CascadeSelections<TType extends CascadeEntityType> = Record<
  CascadeScope<TType>,
  boolean
>;

type CascadeOverrides<TType extends CascadeEntityType> = Partial<
  Record<CascadeScope<TType>, boolean>
>;

type UseCascadeSelectionsArgs<TType extends CascadeEntityType> = {
  entityType: TType;
  mode: CascadeMode;
  overrides?: CascadeOverrides<TType>;
};

type UseCascadeSelectionsResult<TType extends CascadeEntityType> = {
  scopes: CascadeScope<TType>[];
  selections: CascadeSelections<TType>;
  defaults: CascadeSelections<TType>;
  setSelection: (scope: CascadeScope<TType>, enabled: boolean) => void;
  toggleSelection: (scope: CascadeScope<TType>) => void;
  setSelections: (next: CascadeSelections<TType>) => void;
  resetSelections: () => void;
  isScopeAvailable: (scope: CascadeScope<TType>) => boolean;
};

const DEFAULT_SCOPE_AVAILABILITY = {
  album: { album: true, recordings: true, tracks: true },
  recording: { recording: true, tracks: true },
  playlist: { playlist: true, recordings: true, tracks: true },
  work: { work: true, recordings: true },
} as const;

export const useCascadeSelections = <TType extends CascadeEntityType>({
  entityType,
  mode,
  overrides,
}: UseCascadeSelectionsArgs<TType>): UseCascadeSelectionsResult<TType> => {
  const defaultsByMode = useAppSelector((state) => {
    switch (entityType) {
      case 'album':
        return selectAlbumCascadeDefaults(state);
      case 'recording':
        return selectRecordingCascadeDefaults(state);
      case 'work':
        return selectWorkCascadeDefaults(state);
      case 'playlist':
      default:
        return selectPlaylistCascadeDefaults(state);
    }
  }) as Record<CascadeMode, CascadeSelections<TType>>;

  const defaultSelections = useMemo<CascadeSelections<TType>>(() => {
    const scopedDefaults = defaultsByMode[mode];
    const result = {} as CascadeSelections<TType>;
    (ENTITY_SCOPES[entityType] as readonly CascadeScope<TType>[]).forEach((scope) => {
      const override = overrides?.[scope];
      result[scope] = override ?? scopedDefaults[scope];
    });
    return result;
  }, [defaultsByMode, entityType, mode, overrides]);

  const [selections, setSelectionsState] =
    useState<CascadeSelections<TType>>(defaultSelections);

  useEffect(() => {
    setSelectionsState(defaultSelections);
  }, [defaultSelections]);

  const setSelection = (scope: CascadeScope<TType>, enabled: boolean) => {
    setSelectionsState((previous) => {
      if (previous[scope] === enabled) {
        return previous;
      }
      return { ...previous, [scope]: enabled };
    });
  };

  const toggleSelection = (scope: CascadeScope<TType>) => {
    setSelectionsState((previous) => ({ ...previous, [scope]: !previous[scope] }));
  };

  const resetSelections = () => {
    setSelectionsState(defaultSelections);
  };

  const setSelections = (next: CascadeSelections<TType>) => {
    setSelectionsState(next);
  };

  const isScopeAvailable = (scope: CascadeScope<TType>) =>
    (DEFAULT_SCOPE_AVAILABILITY[entityType] as Record<CascadeScope<TType>, boolean>)[scope];

  return {
    scopes: [...ENTITY_SCOPES[entityType]],
    selections,
    defaults: defaultSelections,
    setSelection,
    toggleSelection,
    setSelections,
    resetSelections,
    isScopeAvailable,
  };
};
